// src/annihilation/profileWrites.js
// ============================================================
//  ANNIHILATION — profile write lane. (v2 — Batch 6)
//
//  WRITE-LANE LAW (IE-wide, non-negotiable):
//  Annihilation writes ONLY to:
//    · profiles/{uid}/annihilation___*   (per-player lane)
//    · annihilation/*                    (world/net lane)
//  Everything else on the shared profile is READ-ONLY from here.
//  Divine Crests are read-only in this app; DC grants route via
//  the grant queue and are credited by Pierre.
//
//  CURRENCY — BLOOD MONEY (caP'n, July 10 2026):
//  Annihilation's own currency. Follows the IE lane pattern:
//  this app owns the ONE write path for Blood Money; every other
//  app reads only; exchange (if/when it exists) centralizes at
//  Pierre's Crest Well — never here.
//
//  HARD LAWS honored below:
//  · Currency changes are DELTA TRANSACTIONS — never full-object
//    overwrites (the Apr 2026 crest-wipe rule).
//  · Grant-before-flag for any reward claim.
//
//  v2 (Batch 6) adds three character-lane write paths the field
//  needs — all subfields of annihilation___character (already on
//  the hardening whitelist; no new top-level nodes):
//    · deltaMaterial     — gathering (Ashen Moss, Fold-Iron, …)
//    · markZoneVisited   — first-time-in-zone XP gate
//    · recordDungeonClear — clear counters (Batch 8 derives owed
//      Tome drops from these; grant-before-flag kept whole)
//
//  ⚑ HARDENING WHITELIST — nodes introduced by this file:
//    profiles/{uid}/annihilation___bloodMoney
//    profiles/{uid}/annihilation___character
//  (plus annihilation/village/{players,ready,challenges} from
//  Plaza.js). All must be whitelisted in the same rules deploy
//  when hardening lands, or writes die silently.
// ============================================================

import { ref, runTransaction, update, get } from "firebase/database";
import { db } from "./firebase";

// One lane prefix. One truth. Never build these paths by hand
// at a call site — route through the helpers below.
export const LANE = "annihilation___";

const bloodMoneyRef = (uid) => ref(db, `profiles/${uid}/${LANE}bloodMoney`);
const characterRef = (uid) => ref(db, `profiles/${uid}/${LANE}character`);

// ------------------------------------------------------------
// BLOOD MONEY
// ------------------------------------------------------------

/**
 * Apply a signed delta to Blood Money. THE only way this app
 * changes the balance. Clamps at zero — a spend that would go
 * negative is rejected (returns { ok: false }).
 *
 * @param {string} uid
 * @param {number} delta  positive = earn, negative = spend
 * @returns {Promise<{ ok: boolean, balance: number|null }>}
 */
export async function deltaBloodMoney(uid, delta) {
  if (!uid || !Number.isFinite(delta)) return { ok: false, balance: null };
  let rejected = false;
  const result = await runTransaction(bloodMoneyRef(uid), (current) => {
    const cur = typeof current === "number" ? current : 0;
    const next = cur + delta;
    if (next < 0) {
      rejected = true;
      return; // abort — insufficient Blood Money
    }
    rejected = false;
    return next;
  });
  const balance = result.snapshot?.val();
  return {
    ok: result.committed && !rejected,
    balance: typeof balance === "number" ? balance : null,
  };
}

/**
 * Initialize the Blood Money node if it has never existed.
 * Idempotent — an existing balance is never touched.
 * Starting balance is 0; the welcome amount (if any) is a
 * caP'n tuning call, not a code decision.
 */
export async function ensureBloodMoney(uid, startingBalance = 0) {
  if (!uid) return;
  await runTransaction(bloodMoneyRef(uid), (current) =>
    typeof current === "number" ? current : startingBalance
  );
}

// ------------------------------------------------------------
// CHARACTER LANE
// ------------------------------------------------------------
// The Annihilation character (being, level, xp, skill points,
// spent nodes, position) lives at annihilation___character.
// Writes are FIELD-LEVEL updates, never whole-object set() —
// same discipline as currency, so two systems saving different
// fields can never wipe each other.

/** Read the character once (null if none exists yet). */
export async function getCharacter(uid) {
  if (!uid) return null;
  const snap = await get(characterRef(uid));
  return snap.exists() ? snap.val() : null;
}

/**
 * Patch character fields. update() merges at the field level —
 * pass only what changed. Example:
 *   saveCharacter(uid, { level: 7, xp: 12750 })
 */
export async function saveCharacter(uid, fields) {
  if (!uid || !fields || typeof fields !== "object") return;
  await update(characterRef(uid), { ...fields, updatedAt: Date.now() });
}

/**
 * XP is additive from many sources at once (kills + quests +
 * gathering can land in the same second) — so XP, like currency,
 * moves by delta transaction, not by read-modify-write.
 */
export async function deltaXp(uid, amount) {
  if (!uid || !Number.isFinite(amount) || amount <= 0) return null;
  const xpRef = ref(db, `profiles/${uid}/${LANE}character/xp`);
  const result = await runTransaction(xpRef, (current) =>
    (typeof current === "number" ? current : 0) + amount
  );
  const v = result.snapshot?.val();
  return typeof v === "number" ? v : null;
}

// ------------------------------------------------------------
// MATERIALS (Batch 6 — gathering nodes, Systems Spec Part 7)
// ------------------------------------------------------------
// Counts live at annihilation___character/materials/{materialId}.
// Same law as XP and currency: gathers can race (two nodes
// tapped in the same second), so counts move by delta
// transaction, clamped ≥ 0.

/**
 * @param {string} uid
 * @param {string} materialId  internal id, e.g. "ashen_moss"
 * @param {number} delta       positive = gather, negative = spend (crafting, Batch 16+)
 * @returns {Promise<{ ok: boolean, count: number|null }>}
 */
export async function deltaMaterial(uid, materialId, delta) {
  if (!uid || !materialId || !Number.isFinite(delta)) {
    return { ok: false, count: null };
  }
  let rejected = false;
  const matRef = ref(
    db,
    `profiles/${uid}/${LANE}character/materials/${materialId}`
  );
  const result = await runTransaction(matRef, (current) => {
    const cur = typeof current === "number" ? current : 0;
    const next = cur + delta;
    if (next < 0) {
      rejected = true;
      return; // abort — can't spend materials you don't hold
    }
    rejected = false;
    return next;
  });
  const count = result.snapshot?.val();
  return {
    ok: result.committed && !rejected,
    count: typeof count === "number" ? count : null,
  };
}

// ------------------------------------------------------------
// ZONE DISCOVERY (Batch 6 — first-time-in-zone XP, Spec Part 4)
// ------------------------------------------------------------
// Flags live at annihilation___character/zonesVisited/{zoneId}.
// The transaction IS the gate: it only commits the flag if it
// wasn't already set, so the first-time bonus can never double-
// fire even across two racing tabs. Grant-before-flag stays
// honest because the caller awards XP only when this returns
// firstVisit: true — and a failed XP write after a set flag
// costs one bonus, never a dupe (the conservative direction).
//
// @returns {Promise<{ firstVisit: boolean }>}
export async function markZoneVisited(uid, zoneId) {
  if (!uid || !zoneId) return { firstVisit: false };
  let first = false;
  const zRef = ref(db, `profiles/${uid}/${LANE}character/zonesVisited/${zoneId}`);
  await runTransaction(zRef, (current) => {
    if (current === true) {
      first = false;
      return; // abort — already discovered
    }
    first = true;
    return true;
  });
  return { firstVisit: first };
}

// ------------------------------------------------------------
// DUNGEON CLEARS (Batch 6 — the Sunken Fold)
// ------------------------------------------------------------
// Counters live at annihilation___character/dungeonClears/{id}.
// The counter is the drop ledger: Batch 8 (Tomes + Willow's
// Forge) derives owed boss-Tome drops from clears instead of
// inventing a parallel pendingTomes node. Derive, don't store.
//
// @returns {Promise<{ clears: number|null, firstClear: boolean }>}
export async function recordDungeonClear(uid, dungeonId) {
  if (!uid || !dungeonId) return { clears: null, firstClear: false };
  const cRef = ref(
    db,
    `profiles/${uid}/${LANE}character/dungeonClears/${dungeonId}`
  );
  const result = await runTransaction(cRef, (current) =>
    (typeof current === "number" ? current : 0) + 1
  );
  const clears = result.snapshot?.val();
  return {
    clears: typeof clears === "number" ? clears : null,
    firstClear: clears === 1,
  };
}
